import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import { enUS, es, ptBR } from 'date-fns/locale';

/**
 * Formats the passed in date and string
 * @param date the date to format
 * @param dateFormat the string to represent the format
 * @param locale the locale to use for formatting (en, pt, es)
 * @returns a formatted date
 */

const localeMap = {
  en: enUS,
  pt: ptBR,
  es: es,
};

export function FormatDate(
  date: Date,
  dateFormat = 'yyyy-MM-dd',
  locale?: 'en' | 'pt' | 'es'
): string {
  if (!date) {
    return '';
  }
  const dateObj = new Date(date);
  if (!isValid(dateObj)) {
    return '';
  }
  return format(dateObj, dateFormat, {
    locale: locale ? localeMap[locale] : undefined,
  });
}

/**
 * Returns the date format string for a given locale
 * @param locale the locale to use (en, pt, es)
 */
export function getDateFormat(locale: 'en' | 'pt' | 'es'): string {
  const localeCode = locale || 'en';
  const parts = new Intl.DateTimeFormat(localeCode)
    .formatToParts(new Date(2020, 11, 31))
    .map((p) => (p.type === 'literal' ? p.value : p.type));
  return parts
    .map((p) => {
      if (p === 'day') return 'dd';
      if (p === 'month') return 'MM';
      if (p === 'year') return 'yyyy';
      return p;
    })
    .join('');
}

/**
 * Returns the week day names for a given locale
 * @param locale the locale to use (en, pt, es)
 */
export function getWeekDays(locale: 'en' | 'pt' | 'es'): string[] {
  const baseDate = new Date(2020, 5, 7); // Sunday
  return Array.from({ length: 7 }).map((_, i) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(
      new Date(baseDate.getTime() + i * 86400000)
    )
  );
}

/**
 * Returns the month names for a given locale
 * @param locale the locale to use (en, pt, es)
 */
export function getMonths(locale: 'en' | 'pt' | 'es'): string[] {
  return Array.from({ length: 12 }).map((_, i) =>
    new Intl.DateTimeFormat(locale, { month: 'long' }).format(
      new Date(2020, i, 1)
    )
  );
}
